// ClienteOnboarding - Wizard de bienvenida para nuevos clientes
// Ruta: /clientes/onboarding (protegida)
// Sigue el patrón de CRMOnboarding.tsx

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { useClienteFichaQuery } from '@/hooks/useClienteExpediente';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  RocketLaunchIcon,
  ClipboardDocumentListIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const ONBOARDING_STORAGE_KEY = 'migro_client_onboarding_completed';

const STEPS = [
  { id: 'welcome', title: 'welcome.step1Title', icon: RocketLaunchIcon },
  { id: 'expediente', title: 'welcome.step2Title', icon: ClipboardDocumentListIcon },
  { id: 'features', title: 'welcome.step3Title', icon: Squares2X2Icon },
  { id: 'done', title: 'welcome.step4Title', icon: CheckCircleIcon },
] as const;

type StepId = (typeof STEPS)[number]['id'];

export function hasCompletedClientOnboarding(): boolean {
  return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
}

export function resetClientOnboarding(): void {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
}

export function ClienteOnboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { user } = useAuth();
  const { data: ficha, isLoading: fichaLoading } = useClienteFichaQuery();

  const [currentStep, setCurrentStep] = useState<StepId>('welcome');
  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  const goNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex].id);
    }
  };

  const goBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].id);
    }
  };

  const handleFinish = () => {
    try { localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true'); } catch { /* quota/private */ }
    navigate('/clientes/tramite', { replace: true });
  };

  const handleSkip = () => {
    try { localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true'); } catch { /* quota/private */ }
    navigate('/clientes/tramite', { replace: true });
  };

  const completitudPct = ficha?.expediente_workspace?.completitud_pct;
  const expedienteStatus = ficha?.expediente_workspace?.status;

  const FEATURES = [
    { icon: ClipboardDocumentListIcon, key: 'featureProcess', color: 'bg-indigo-500' },
    { icon: DocumentTextIcon, key: 'featureContracts', color: 'bg-blue-500' },
    { icon: DocumentDuplicateIcon, key: 'featureInvoices', color: 'bg-purple-500' },
    { icon: CalendarDaysIcon, key: 'featureAppointments', color: 'bg-teal-500' },
    { icon: UserCircleIcon, key: 'featureProfile', color: 'bg-amber-500' },
    { icon: QuestionMarkCircleIcon, key: 'featureHelp', color: 'bg-gray-500' },
  ];

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((step, i) => {
              const isCompleted = i < currentStepIndex;
              const isCurrent = step.id === currentStep;
              const Icon = step.icon;
              return (
                <div key={step.id} className="flex items-center gap-2">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                      ${isCompleted ? 'bg-green-600 text-white' : ''}
                      ${isCurrent ? 'bg-green-600 text-white ring-4 ring-green-100 dark:ring-green-900' : ''}
                      ${!isCompleted && !isCurrent ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500' : ''}
                    `}
                  >
                    {isCompleted ? (
                      <CheckCircleIcon className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`hidden sm:block w-16 lg:w-24 h-1 rounded-full transition-all duration-300 ${
                        isCompleted ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <motion.div
              className="bg-green-600 h-1.5 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* ============ STEP 1: WELCOME ============ */}
            {currentStep === 'welcome' && (
              <Card className="shadow-xl">
                <CardContent className="pt-8 pb-8 text-center space-y-6">
                  <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <RocketLaunchIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {user.full_name
                      ? t('onboarding.welcome.greetingName', { name: user.full_name })
                      : t('onboarding.welcome.greeting')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    {t('onboarding.welcome.introText')}
                  </p>
                  <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={goNext} className="gap-2">
                      {t('onboarding.welcome.letsStart')}
                      <ArrowRightIcon className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleSkip} className="text-gray-400">
                      {t('onboarding.welcome.skip')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ============ STEP 2: EXPEDIENTE ============ */}
            {currentStep === 'expediente' && (
              <Card className="shadow-xl">
                <CardContent className="pt-8 pb-8 space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {t('onboarding.welcome.expedienteTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('onboarding.welcome.expedienteDesc')}
                    </p>
                  </div>

                  {fichaLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                    </div>
                  ) : ficha?.expediente_workspace ? (
                    <div className="bg-green-50 dark:bg-green-950 rounded-lg p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('onboarding.welcome.expedienteStatus')}
                        </span>
                        <Badge variant="secondary" className="capitalize">
                          {expedienteStatus || 'nuevo'}
                        </Badge>
                      </div>
                      {completitudPct != null && (
                        <div>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600 dark:text-gray-400">
                              {t('onboarding.welcome.expedienteCompletion')}
                            </span>
                            <span className="font-medium text-green-600">{completitudPct}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full transition-all"
                              style={{ width: `${completitudPct}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-5 text-center">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {t('onboarding.welcome.expedienteNoData')}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={goBack} className="gap-2">
                      <ArrowLeftIcon className="w-4 h-4" />
                      {t('onboarding.welcome.back')}
                    </Button>
                    <Button onClick={goNext} className="gap-2">
                      {t('onboarding.welcome.next')}
                      <ArrowRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ============ STEP 3: FEATURES ============ */}
            {currentStep === 'features' && (
              <Card className="shadow-xl">
                <CardContent className="pt-8 pb-8 space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {t('onboarding.welcome.featuresTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('onboarding.welcome.featuresDesc')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {FEATURES.map(({ icon: Icon, key, color }) => (
                      <div
                        key={key}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center space-y-2 hover:shadow-md transition-shadow"
                      >
                        <div className={`mx-auto w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {t(`onboarding.welcome.${key}`)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t(`onboarding.welcome.${key}Desc`)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={goBack} className="gap-2">
                      <ArrowLeftIcon className="w-4 h-4" />
                      {t('onboarding.welcome.back')}
                    </Button>
                    <Button onClick={goNext} className="gap-2">
                      {t('onboarding.welcome.next')}
                      <ArrowRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ============ STEP 4: DONE ============ */}
            {currentStep === 'done' && (
              <Card className="shadow-xl">
                <CardContent className="pt-8 pb-8 text-center space-y-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 10, delay: 0.2 }}
                    className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center"
                  >
                    <CheckCircleIcon className="w-10 h-10 text-green-600 dark:text-green-400" />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {t('onboarding.welcome.doneTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    {t('onboarding.welcome.doneDesc')}
                  </p>
                  <div className="pt-4">
                    <Button onClick={handleFinish} size="lg" className="gap-2">
                      {t('onboarding.welcome.goToDashboard')}
                      <ArrowRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
